import { useAui } from "@assistant-ui/react";
import { useEffect, useState } from "react";
import { useWorkspace } from "@/hooks/use-workspace";
import { formatRelativeTime } from "@/lib/omnibox";
import { clearHistory, getHistory, matchHistory, type SearchEntry } from "@/lib/search-history";
import { cn } from "@/lib/utils";
import {
  dispatchSearch,
  getCurrentEngine,
  getEngine,
} from "@/routes/dashboard/components/assistant/search-engine-picker";

const DISPLAY_COUNT = 5;

function entrySource(entry: SearchEntry): string {
  if (entry.type === "ai") return entry.engineOrModel;
  return getEngine(entry.engineOrModel)?.label ?? entry.engineOrModel;
}

/**
 * The home's first run: no history yet, so say once, quietly, what the bar
 * does instead of showing an empty list.
 */
function EmptyState({ canAsk, className }: { canAsk: boolean; className?: string }) {
  const engine = getCurrentEngine();
  const lines: [string, string][] = [
    ["An address", "opens the page"],
    ["A few words", engine ? `search ${engine.label}` : "search the web"],
  ];
  if (canAsk) lines.push(["A question", "asks Kourai"]);

  return (
    <section aria-label="What the bar does" className={cn("w-full pt-10", className)}>
      <ul className="border-foreground text-muted-foreground border-t pt-3 text-[13px]">
        {lines.map(([what, does]) => (
          <li key={what} className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 py-1">
            <span className="text-foreground">{what}</span>
            <span>{does}</span>
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground pt-3 text-[13px]">
        The bar guesses which you mean. <kbd className="border-border border px-1">Tab</kbd>{" "}
        switches.
      </p>
    </section>
  );
}

/**
 * "Recent" on the home: the last chats and web searches in this workspace,
 * narrowed to matches from that history while something is typed. Chats
 * reopen their thread; searches run again.
 */
export function RecentActivity({
  types = ["ai", "web"],
  query = "",
  limit = DISPLAY_COUNT,
  className,
}: {
  types?: SearchEntry["type"][];
  query?: string;
  /** Rows to show (the Recent widget's setting). */
  limit?: number;
  className?: string;
}) {
  const { activeOrganizationId } = useWorkspace();
  const aui = useAui();
  const [history, setHistory] = useState<SearchEntry[]>([]);
  const typeKey = types.join(",");
  const canAsk = types.includes("ai");

  // biome-ignore lint/correctness/useExhaustiveDependencies: typeKey stands in for the types array
  useEffect(() => {
    setHistory(getHistory(activeOrganizationId).filter((e) => types.includes(e.type)));
  }, [activeOrganizationId, typeKey]);

  const typing = query.trim().length > 0;
  const entries = typing ? matchHistory(history, query, limit) : history.slice(0, limit);

  if (history.length === 0) return <EmptyState canAsk={canAsk} className={className} />;
  if (entries.length === 0) return null;

  const open = async (entry: SearchEntry) => {
    if (entry.type === "web") {
      dispatchSearch(entry.query, activeOrganizationId);
      return;
    }
    if (entry.threadId) {
      try {
        await aui.threads().switchToThread(entry.threadId);
        return;
      } catch {
        // The thread may have been deleted; fall back to asking again.
      }
    }
    aui.composer().setText(entry.query);
  };

  return (
    <section aria-labelledby="recent-heading" className={cn("w-full pt-10", className)}>
      <div className="border-foreground flex items-baseline justify-between border-t pt-3 pb-1 text-[13px]">
        <h2 id="recent-heading" className="font-normal">
          {typing ? "From your history" : "Recent"}
        </h2>
        {!typing && (
          <button
            type="button"
            onClick={() => {
              clearHistory(activeOrganizationId);
              setHistory([]);
            }}
            className="text-muted-foreground hover:text-brand transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      <ul>
        {entries.map((entry) => (
          <li key={entry.id} className="border-border border-t first:border-t-0">
            <button
              type="button"
              onClick={() => open(entry)}
              className="group grid w-full grid-cols-[3rem_minmax(0,1fr)_auto] items-baseline gap-x-4 py-3 text-start"
            >
              <span className="text-muted-foreground text-[13px]">
                {entry.type === "ai" ? "Chat" : "Web"}
              </span>
              <span className="min-w-0">
                <span className="group-hover:text-brand block truncate text-base transition-colors">
                  {entry.query}
                </span>
                <span className="text-muted-foreground block truncate text-[13px]">
                  {entrySource(entry)}
                </span>
              </span>
              <span className="text-muted-foreground text-[13px] tabular-nums">
                {formatRelativeTime(entry.timestamp)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
